import { useState, useEffect } from 'react';

import './QuestionForm.page.css';
import downArrow from '../../assets/downArrow.png';
import upArrow from '../../assets/upArrow.png';
import { LoginState } from '../../types/LoginState';
import MainTopBar from '../../ui/panels/TopBars/MainTopBar/MainTopBar';
import { useNavigate } from 'react-router-dom';
import { UserUtils } from '../../utils/UserUtils';
import { useParams } from 'react-router-dom';
import UserTokenData from '../../types/UserTokenData';
import { useMemo } from 'react';

interface SectionData {
  sectionId: number;
  sectionName: string;
  TotalQuestions: number;
  NotAnsweredCount: number;
  expanded: boolean;
  questions: QuestionData[];
}

interface QuestionData {
  questionId: number;
  questionText: string;
  answerId: number;
  answerText: string;
  isAnswerEditable: boolean;
  editEnable: boolean;
  originalAnswerText: string;
  isLoading: boolean;
}

interface CaseDetails {
  status: string;
  email: string;
  firstName: string;
  entityId: string;
  lastName: string;
}

interface CitationStatus {
  status: string;
  error: string;
}

interface LetterStatus {
  status: string;
  error: string;
}

interface PatientDetails {
  case: CaseDetails;
  citationStatus: CitationStatus;
  letterStatus: LetterStatus;
}

function QuestionFormPage(): JSX.Element {
  const [token, setToken] = useState<string>(
    localStorage.getItem('token') || ''
  );
  const { userId } = useParams();
  const nav = useNavigate();
  const [loginState, setLoginState] = useState<LoginState>({
    isLoggedIn: false,
    isAdmin: false,
  });
  const [formData, setFormData] = useState<SectionData[]>([]);

  // once we will connect it to patient table we will get the correct data and uncomment it to get the actual patient info
  const [userDetails, setUserDetails] = useState<UserTokenData>({
    userName: '',
    id: '',
    email: '',
    role: '',
  });
  const [patientDetails, setPatientDetails] = useState<PatientDetails>({
    case: { status: '', email: '', firstName: '', entityId: '', lastName: '' },
    citationStatus: { status: 'unknown', error: '' },
    letterStatus: { status: 'unknown', error: '' },
  });

  const [showOnlyUnanswered, setShowOnlyUnanswered] = useState<boolean>(false);

  useEffect(() => {
    if (token == '') {
      nav('/signin');
    }
    const userDetails = UserUtils.decodeUserToken(token);
    if (!userDetails) {
      nav('/signin');
    } else {
      setUserDetails(userDetails);
    }
  }, [token]);
 
  const isUnanswered = (answerText: string) => {

    if (answerText === undefined || answerText === null) return true;
    const ansStr = String(answerText).trim().toLowerCase();
    return (
      ansStr === '' ||
      ansStr.includes('data not available') ||
      ansStr.includes('no answer found') ||
      ansStr.includes('no answer')
    );
  };
  

  const filteredSections = useMemo(() => {
    if (!showOnlyUnanswered) return formData;
    return formData
      .filter((section) =>
        section.questions.some((q) => {
          // If question is being edited, check originalAnswerText to keep it in the list
          const textToCheck = q.editEnable ? q.originalAnswerText : q.answerText;
          return isUnanswered(textToCheck);
        })
      )
      .map((section) => ({
        ...section,
        questions: section.questions.filter((q) => {
          // If question is being edited, check originalAnswerText to keep it in the list
          const textToCheck = q.editEnable ? q.originalAnswerText : q.answerText;
          return isUnanswered(textToCheck);
        }),
      }));
  }, [formData, showOnlyUnanswered]);

  const validateUserLogin = async () => {
    if (token) {
      const url = '/validate';
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`, // Include the token in the Authorization header
        },
      });
      if (!response.ok) {
        setLoginState({ isLoggedIn: false, isAdmin: false });
        nav('/');
        return;
      }
      const json = await response.json();

      if (json.valid && json.valid === true) {
        setLoginState({ isLoggedIn: true, isAdmin: true });
      } else {
        setLoginState({ isLoggedIn: true, isAdmin: false });
      }
    }
  };

  const getAnswers = async () => {
    const url = '/form/sections';
    try {
      const jsonData = {
        userId: userId,
        role: userDetails.role,
      };

      const finalData = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(jsonData),
      });

      if (!finalData.ok) {
        console.error('Error while fetching data:', finalData.statusText);
        return;
      }

      const data = await finalData.json();
      const parsedData: SectionData[] = JSON.parse(data);
      const sectionData = parsedData.map((item) => ({
        sectionId: item.sectionId,
        sectionName: item.sectionName,
        TotalQuestions: item.TotalQuestions,
        NotAnsweredCount: item.NotAnsweredCount,
        expanded: false,
        questions: item.questions.map((q) => ({
          questionId: q.questionId,
          questionText: q.questionText,
          answerId: q.answerId,
          answerText: q.answerText,
          editEnable: false,
          isAnswerEditable: q.isAnswerEditable, // Initialize isAnswerEditable
          originalAnswerText: q.answerText, // Initialize originalAnswerText
          isLoading: false, // Initialize loading state
        })),
      }));

      setFormData(sectionData);
    } catch (error) {
      console.error('Error in getAnswers:', error);
    }
  };

  const getUserDetails = async () => {
    const url = `/admin/caseDetails/${userId}?detailed=false`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`, // Include the token in the Authorization header
      },
    });
    const patientData = await response.json();
    setPatientDetails({
      case: patientData.case,
      letterStatus: { status: patientData.letterStatus, error: '' },
      citationStatus: { status: patientData.citationStatus, error: '' },
    });
  };

  const requestNewCitationJob = async () => {
    // Set loading for page here
    try {
      const url = '/case/citation/jobs';
      const jsonData = {
        entityId: patientDetails.case.entityId,
        requestedBy: userDetails.id,
      };
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`, // Include the token in the Authorization header
        },
        body: JSON.stringify(jsonData),
      });

      if (!response.ok) {
        setPatientDetails((prev) => ({
          ...prev,
          citationStatus: {
            status: prev.citationStatus.status,
            error: 'Network Error',
          },
        }));
        // Turn off loading here
        return;
      }

      setPatientDetails((prev) => ({
        ...prev,
        citationStatus: {
          status: 'Citation will be processed tonight',
          error: '',
        },
      }));
      // Update the original answer text to the new saved text and disable editing
    } catch (error) {
      setPatientDetails((prev) => ({
        ...prev,
        citationStatus: {
          status: prev.citationStatus.status,
          error: 'An error occurred. Try again later.',
        },
      }));
    }
    // Turn off loading here
  };

  const requestNewLetterJob = async () => {
    try {
      const url = '/case/letter/jobs';
      const jsonData = {
        entityId: patientDetails.case.entityId,
        requestedBy: userDetails.id,
      };
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`, // Include the token in the Authorization header
        },
        body: JSON.stringify(jsonData),
      });

      if (!response.ok) {
        setPatientDetails((prev) => ({
          ...prev,
          letterStatus: {
            status: prev.letterStatus.status,
            error: 'Network Error',
          },
        }));
        // Turn off loading here
        return;
      }

      setPatientDetails((prev) => ({
        ...prev,
        letterStatus: {
          status: 'Letter will be processed tonight',
          error: '',
        },
      }));
      // Update the original answer text to the new saved text and disable editing
    } catch (error) {
      setPatientDetails((prev) => ({
        ...prev,
        letterStatus: {
          status: prev.letterStatus.status,
          error: 'An error occurred. Try again later.',
        },
      }));
    }
  };

  const toggleSection = (sectionId: number) => {
    setFormData((prev) =>
      prev.map((s) =>
        s.sectionId === sectionId ? { ...s, expanded: !s.expanded } : s
      )
    );
  };

  const toggleEdit = (sectionId: number, questionId: number) => {
    setFormData((prev) => {
      const sectionIdx = prev.findIndex((s) => s.sectionId === sectionId);
      if (sectionIdx === -1) return prev;

      const updated = [...prev]; // shallow copy sections
      const updatedSection = { ...updated[sectionIdx] }; // copy target section
      const updatedQuestions = [...updatedSection.questions]; // copy questions

      const questionIndex = updatedQuestions.findIndex((q) => q.questionId === questionId);
      if (questionIndex === -1) return prev;

      updatedQuestions[questionIndex] = {
        ...updatedQuestions[questionIndex],
        editEnable: true, // enable editing for the question
      };

      updatedSection.questions = updatedQuestions;
      updated[sectionIdx] = updatedSection;
      return updated;
    });
  };

  const handleSave = async (sectionId: number, questionId: number) => {
    // Find the section and question by their IDs
    const sectionIdx = formData.findIndex((s) => s.sectionId === sectionId);
    if (sectionIdx === -1) {
      throw new Error('Section not found'); //TODO: need to correct Error handling here
    }

    const questionIndex = formData[sectionIdx].questions.findIndex((q) => q.questionId === questionId);
    if (questionIndex === -1) {
      throw new Error('Question not found'); //TODO: need to correct Error handling here
    }

    const question = formData[sectionIdx].questions[questionIndex];

    if (!question) {
      throw new Error('Question not found'); //TODO: need to correct Error handling here
    }

    // Set loading state to true to prevent duplicate submissions
    setFormData((prev) => {
      const updated = [...prev];
      const updatedSection = { ...updated[sectionIdx] };
      const updatedQuestions = [...updatedSection.questions];

      updatedQuestions[questionIndex] = {
        ...updatedQuestions[questionIndex],
        isLoading: true,
      };

      updatedSection.questions = updatedQuestions;
      updated[sectionIdx] = updatedSection;
      return updated;
    });

    try {
      const url = '/form/update-answer';
      const jsonData = {
        answerId: question.answerId,
        answerText: question.answerText,
        userId: userDetails.id,
      };
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`, // Include the token in the Authorization header
        },
        body: JSON.stringify(jsonData),
      });

      if (!response.ok) {
        console.error('Error updating answer:', response.statusText);
        // TODO: Handle error (show user notification, revert changes, etc.)
        // Reset loading state on error
        setFormData((prev) => {
          const updated = [...prev];
          const updatedSection = { ...updated[sectionIdx] };
          const updatedQuestions = [...updatedSection.questions];

          updatedQuestions[questionIndex] = {
            ...updatedQuestions[questionIndex],
            isLoading: false,
          };

          updatedSection.questions = updatedQuestions;
          updated[sectionIdx] = updatedSection;
          return updated;
        });
        return;
      }

      const result = await response.json();

      // Update the original answer text to the new saved text and disable editing
      setFormData((prev) => {
        const updated = [...prev]; // shallow copy sections
        const updatedSection = { ...updated[sectionIdx] }; // copy the target section
        const updatedQuestions = [...updatedSection.questions]; // copy its questions

        updatedQuestions[questionIndex] = {
          ...updatedQuestions[questionIndex],
          originalAnswerText: result.answerText,
          editEnable: false, // disable editing after successful save
          isLoading: false, // reset loading state
        };

        updatedSection.questions = updatedQuestions;
        updated[sectionIdx] = updatedSection;
        return updated;
      });
    } catch (error) {
      console.error('Error in updateAnswer:', error);
      // TODO: Handle error (show user notification, revert changes, etc.)
      // Reset loading state on error
      setFormData((prev) => {
        const updated = [...prev];
        const updatedSection = { ...updated[sectionIdx] };
        const updatedQuestions = [...updatedSection.questions];

        updatedQuestions[questionIndex] = {
          ...updatedQuestions[questionIndex],
          isLoading: false,
        };

        updatedSection.questions = updatedQuestions;
        updated[sectionIdx] = updatedSection;
        return updated;
      });
    }
  };

  const handleCancel = (sectionId: number, questionId: number) => {
    setFormData((prev) => {
      const sectionIdx = prev.findIndex((s) => s.sectionId === sectionId);
      if (sectionIdx === -1) return prev;

      const updated = [...prev]; // shallow copy sections
      const updatedSection = { ...updated[sectionIdx] }; // copy target section
      const updatedQuestions = [...updatedSection.questions]; // copy questions

      const questionIndex = updatedQuestions.findIndex((q) => q.questionId === questionId);
      if (questionIndex === -1) return prev;

      updatedQuestions[questionIndex] = {
        ...updatedQuestions[questionIndex],
        answerText: updatedQuestions[questionIndex].originalAnswerText, // revert to original text
        editEnable: false, // disable editing for the cancelled question
      };

      updatedSection.questions = updatedQuestions;
      updated[sectionIdx] = updatedSection;
      return updated;
    });
  };

  const handleAnswerChange = (questionId: number, newAnswerText: string) => {
    setFormData((prev) =>
      prev.map((section) => ({
        ...section,
        questions: section.questions.map((q) =>
          q.questionId === questionId ? { ...q, answerText: newAnswerText } : q
        ),
      }))
    );
  };

  const autoResizeTextarea = (textarea: HTMLTextAreaElement) => {
    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto';
    const computedStyle = window.getComputedStyle(textarea);
    const maxHeight = parseInt(computedStyle.maxHeight);
    textarea.style.height = Math.min(textarea.scrollHeight, maxHeight) + 'px';
  };

  // Run these functions only after userDetails has been populated
  useEffect(() => {
    if (userDetails.id) {
      validateUserLogin();
      getAnswers();
      getUserDetails();
    }
  }, [userDetails.id]);

  // TODO need to componentize items in the react below
  return (
    <div className="userAnswerBody">
      <MainTopBar
        isLoggedIn={loginState.isLoggedIn}
        isSticky={true}
        showAdminButton={loginState.isAdmin}
        userName={userDetails.userName}
      />
      <div className="patientDetailsContainer">
        <h1>Welcome to Scribe</h1>
        <div className="patientDetails">
          <p>
            Patient Name:{' '}
            <span>
              {patientDetails.case.firstName} {patientDetails.case.lastName}
            </span>
          </p>
          <p>
            Status:{' '}
            <span className="patientStatus">{patientDetails.case.status}</span>{' '}
          </p>
        </div>
      </div>
      <div className="patientDetailsContainer">
        <div className='patientDetailsContainer-btns'>
        {/* <button onClick={requestNewCitationJob}>Create Citations</button>
        <button onClick={requestNewLetterJob}>Create Letter</button> */}
        </div>
        <label>
      <input
        type="checkbox"
        checked={showOnlyUnanswered}
        onChange={(e) => setShowOnlyUnanswered(e.target.checked)}
      />
      {' '}Show unanswered questions
    </label>
      </div>

      <div className="answersContainer">
        {filteredSections.map((section, sIdx) => (
          <div key={section.sectionId}>
            <div
              className={`sectionAssigneeContainer ${section.expanded ? 'activeSection' : ''}`}
            >
              <div className="sectionName">
                <h6
                  className={`sectionTitle ${section.expanded ? 'activeSection' : ''}`}
                  onClick={() => toggleSection(section.sectionId)}
                >
                  {section.sectionName}
                  <img
                    src={section.expanded ? upArrow : downArrow}
                    alt="arrow"
                    className="sectionArrow"
                  />
                </h6>
              </div>
              <div className="sectionProgressContainer">
                <span className="sectionProgressText">
                  {section.TotalQuestions - section.NotAnsweredCount} of{' '}
                  {section.TotalQuestions}
                </span>
                <div className="sectionProgress">
                  <div
                    className="sectionProgressFill"
                    style={{
                      width: `${Math.min(100, Math.max(0, ((section.TotalQuestions - section.NotAnsweredCount) / section.TotalQuestions) * 100))}%`,
                    }}
                  ></div>
                </div>
                <span className="sectionProgressText">
                  {Math.round(
                    ((section.TotalQuestions - section.NotAnsweredCount) /
                      section.TotalQuestions) *
                      100
                  )}
                  %
                </span>
              </div>
            </div>
            {section.expanded && (
              <ul className="questionAnswerList">
                {section.questions.map((question, qIdx) => (
                  <li key={question.questionId} className="sectionItem">
                    <div className="questionAnswerContainer">
                      <h6> {question.questionText} </h6>
                      {/* TODO: Make this textArea as a component to use it on other pages */}
                      <textarea
                        className={`answerTextArea ${question.editEnable ? 'inputEditable' : ''}`}
                        value={question.answerText}
                        disabled={!question.editEnable || question.isLoading}
                        onChange={(e) => {
                          handleAnswerChange(
                            question.questionId,
                            e.target.value
                          );
                          autoResizeTextarea(e.target);
                        }}
                        ref={(textarea) => {
                          if (textarea) {
                            autoResizeTextarea(textarea);
                          }
                        }}
                      />
                    </div>
                    <div className="answerEditButtons">
                      {question.isAnswerEditable &&
                        question.answerId &&
                        (question.editEnable ? (
                          <div className="answerEditButtons">
                            <button
                              className="saveButton"
                              onClick={() => handleSave(section.sectionId, question.questionId)}
                              disabled={
                                question.isLoading ||
                                question.answerText ===
                                  question.originalAnswerText
                              }
                            >
                              {question.isLoading ? 'Saving...' : 'Save'}
                            </button>

                            <button
                              className="cancelButton"
                              onClick={() => handleCancel(section.sectionId, question.questionId)}
                              disabled={question.isLoading}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            className="editButton"
                            onClick={() => toggleEdit(section.sectionId, question.questionId)}
                            disabled={question.isLoading}
                          >
                            Edit
                          </button>
                        ))}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>

      <div className="questionFormButtonContainer">
        <button className="questionFormCitationButton">Approve</button>
        <button
          className="questionFormCitationButton"
          onClick={() => nav(`/citation/${userId}`)}
        >
          View Citations
        </button>
        <button
          className="questionFormCitationButton"
          onClick={() => nav(`/combined-report/${userId}`)}
        >
          View and Download Letter
        </button>
        <button className="questionFormCitationButton rejectButton">
          Reject
        </button>
      </div>
    </div>
  );
}

export default QuestionFormPage;
